import apiClient from './apiClient';
import type {
    TestConfiguration,
    UserPrompt,
    UserPromptVersion,
    CreateUserPromptDto,
    UpdateUserPromptDto,
} from '../types/types';

const promptsService = {
    getTestConfigurations: async (): Promise<TestConfiguration[]> => {
        const res = await apiClient.get<TestConfiguration[]>('/test-configurations');
        return res.data;
    },

    getTestConfigurationById: async (id: number): Promise<TestConfiguration> => {
        const res = await apiClient.get<TestConfiguration>(`/test-configurations/${id}`);
        return res.data;
    },

    getUserPrompts: async (
        userId: number,
        testConfigurationId?: number,
    ): Promise<UserPrompt[]> => {
        const params = new URLSearchParams({ userId: String(userId) });
        if (testConfigurationId != null) params.set('testConfigurationId', String(testConfigurationId));
        const res = await apiClient.get<UserPrompt[]>(`/user-prompts?${params}`);
        return res.data;
    },

    getAllPromptsForAdmin: async (
        userId?: number,
        testConfigurationId?: number,
        page = 0,
        pageSize = 20,
    ): Promise<{ items: UserPrompt[]; total: number }> => {
        const params = new URLSearchParams({
            page: String(page),
            pageSize: String(pageSize),
        });
        if (userId != null) params.set('userId', String(userId));
        if (testConfigurationId != null) params.set('testConfigurationId', String(testConfigurationId));
        const res = await apiClient.get<{ items: UserPrompt[]; total: number }>(
            `/user-prompts/admin/all?${params}`,
        );
        return res.data;
    },

    getUserPrompt: async (id: number, userId?: number): Promise<UserPrompt> => {
        const params = userId != null ? `?userId=${userId}` : '';
        const res = await apiClient.get<UserPrompt>(`/user-prompts/${id}${params}`);
        return res.data;
    },

    createUserPrompt: async (dto: CreateUserPromptDto): Promise<UserPrompt> => {
        const res = await apiClient.post<UserPrompt>('/user-prompts', dto);
        return res.data;
    },

    createPromptForAll: async (dto: Omit<CreateUserPromptDto, 'userId'>): Promise<{ created: number; prompts: UserPrompt[] }> => {
        const res = await apiClient.post<{ created: number; prompts: UserPrompt[] }>('/user-prompts/admin/create-for-all', dto);
        return res.data;
    },

    migrateGlobalDuplicates: async (): Promise<{
        globalCreated: number;
        groupsFound: number;
        skipped: number;
    }> => {
        const res = await apiClient.post<{
            globalCreated: number;
            groupsFound: number;
            skipped: number;
        }>('/user-prompts/admin/migrate-global-duplicates');
        return res.data;
    },

    updateUserPrompt: async (
        id: number,
        dto: UpdateUserPromptDto,
        userId?: number,
    ): Promise<UserPrompt> => {
        const params = userId != null ? `?userId=${userId}` : '';
        const res = await apiClient.put<UserPrompt>(`/user-prompts/${id}${params}`, dto);
        return res.data;
    },

    deleteUserPrompt: async (id: number, userId?: number): Promise<{ message: string }> => {
        const params = userId != null ? `?userId=${userId}` : '';
        const res = await apiClient.delete<{ message: string }>(`/user-prompts/${id}${params}`);
        return res.data;
    },

    createBranch: async (
        promptId: number,
        name?: string,
        userId?: number,
    ): Promise<UserPrompt> => {
        const params = userId != null ? `?userId=${userId}` : '';
        const res = await apiClient.post<UserPrompt>(`/user-prompts/${promptId}/branch${params}`, {
            name: name ?? undefined,
        });
        return res.data;
    },

    getVersions: async (promptId: number, userId?: number): Promise<UserPromptVersion[]> => {
        const params = userId != null ? `?userId=${userId}` : '';
        const res = await apiClient.get<UserPromptVersion[]>(
            `/user-prompts/${promptId}/versions${params}`,
        );
        return res.data;
    },

    getBranches: async (promptId: number, userId?: number): Promise<UserPrompt[]> => {
        const params = userId != null ? `?userId=${userId}` : '';
        const res = await apiClient.get<UserPrompt[]>(`/user-prompts/${promptId}/branches${params}`);
        return res.data;
    },

    setCurrentVersion: async (
        promptId: number,
        versionId: number,
        userId?: number,
    ): Promise<UserPrompt> => {
        const params = userId != null ? `?userId=${userId}` : '';
        const res = await apiClient.patch<UserPrompt>(
            `/user-prompts/${promptId}/current-version${params}`,
            { versionId },
        );
        return res.data;
    },
};

export default promptsService;
